/**
 * Primitivas visuais da tela Configurações da Produção (Sprint P5).
 *
 * Field-row pattern do brief §1.4 (label 38% · controle+meta 62%, microcopy
 * do efeito abaixo, meta-linha mono `default · escopo` + chip de origem),
 * rail vertical de domínios, savebar sticky por seção e toggle switch.
 * Tudo via tokens do tema — dark mode automático.
 */
import React from "react";
import styled, { css, keyframes } from "styled-components";

// ---------------------------------------------------------------------------
// Layout: rail de domínios (esquerda) + zona principal
// ---------------------------------------------------------------------------

export const LayoutConfig = styled.div`
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 16px;
  align-items: start;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

export const Rail = styled.nav`
  position: sticky;
  top: 76px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 8px;
  max-height: calc(100vh - 120px);
  overflow-y: auto;

  @media (max-width: 860px) {
    position: static;
    max-height: none;
  }
`;

export const RailTitulo = styled.div`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ink-4);
  padding: 10px 10px 4px;
`;

export const RailItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: none;
  background: ${({ $ativo }) => ($ativo ? "var(--accent-soft)" : "transparent")};
  color: ${({ $ativo, $roadmap }) =>
    ($ativo && "var(--accent)") || ($roadmap ? "var(--ink-4)" : "var(--ink-2)")};
  font-family: inherit;
  font-size: 13px;
  font-weight: ${({ $ativo }) => ($ativo ? 600 : 500)};
  text-align: left;
  padding: 8px 10px;
  border-radius: 8px;
  border-left: 3px solid
    ${({ $ativo }) => ($ativo ? "var(--accent)" : "transparent")};
  cursor: pointer;

  &:hover {
    background: ${({ $ativo }) =>
      $ativo ? "var(--accent-soft)" : "var(--surface-2)"};
  }

  .rotulo {
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const ZonaPrincipal = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

// ---------------------------------------------------------------------------
// Field-row (§1.4) — gramática fixa repetida em TODAS as seções
// ---------------------------------------------------------------------------

const pulsoEncontrado = keyframes`
  0% { box-shadow: 0 0 0 0 var(--accent); }
  60% { box-shadow: 0 0 0 4px transparent; }
  100% { box-shadow: 0 0 0 0 transparent; }
`;

export const FieldRowBox = styled.div`
  display: grid;
  grid-template-columns: 38% minmax(0, 1fr);
  gap: 6px 18px;
  padding: 14px 4px;
  border-bottom: 1px solid var(--border-subtle);
  border-radius: 8px;

  &:last-child {
    border-bottom: none;
  }

  ${({ $encontrado }) =>
    $encontrado
    && css`
      animation: ${pulsoEncontrado} 1.2s ease-out 1;
      background: var(--accent-soft);
    `}

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const FieldLabelCol = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: var(--ink);
  padding-top: 6px;
`;

export const FieldControleCol = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

/** Microcopy do "efeito" — inline, nunca tooltip escondido (§1.4). */
export const FieldAjuda = styled.p`
  margin: 0;
  font-size: 12.5px;
  color: var(--ink-3);
  line-height: 1.4;
`;

/** Meta-linha mono: `default: X · escopo: fabrica` + chip de origem. */
export const FieldMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--ink-4);
`;

export const FieldErro = styled.p`
  margin: 0;
  font-size: 12px;
  color: var(--danger-text);
`;

export const LinkDiscreto = styled.button`
  border: none;
  background: none;
  color: var(--accent);
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

// ---------------------------------------------------------------------------
// Controles
// ---------------------------------------------------------------------------

export const InputConfig = styled.input`
  padding: 7px 10px;
  border-radius: 8px;
  border: 1px solid ${({ $erro }) => ($erro ? "var(--danger-border)" : "var(--border)")};
  background: var(--surface);
  color: var(--ink);
  font-size: 0.85rem;
  font-family: inherit;
  max-width: 220px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  &:focus {
    outline: 2px solid var(--accent);
    outline-offset: -1px;
    border-color: transparent;
  }
`;

export const SelectConfig = styled.select`
  padding: 7px 10px;
  border-radius: 8px;
  border: 1px solid ${({ $erro }) => ($erro ? "var(--danger-border)" : "var(--border)")};
  background: var(--surface);
  color: var(--ink);
  font-size: 0.85rem;
  font-family: inherit;
  max-width: 320px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

/** Toggles Seg–Dom / chips de múltipla escolha. */
export const ChipToggle = styled.button`
  border: 1px solid
    ${({ $ativo }) => ($ativo ? "var(--accent)" : "var(--border)")};
  background: ${({ $ativo }) => ($ativo ? "var(--accent-soft)" : "var(--surface)")};
  color: ${({ $ativo }) => ($ativo ? "var(--accent)" : "var(--ink-3)")};
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 10px;
  border-radius: 999px;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const LinhaChips = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const Track = styled.span`
  position: relative;
  width: 36px;
  height: 20px;
  border-radius: 999px;
  background: ${({ $on }) => ($on ? "var(--accent)" : "var(--surface-2)")};
  border: 1px solid ${({ $on }) => ($on ? "var(--accent)" : "var(--border)")};
  transition: background 0.15s ease;
  flex-shrink: 0;

  &::after {
    content: "";
    position: absolute;
    top: 2px;
    left: ${({ $on }) => ($on ? "17px" : "2px")};
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: var(--surface);
    box-shadow: var(--shadow-1);
    transition: left 0.15s ease;
  }
`;

const ToggleWrap = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  font-size: 13px;
  color: var(--ink-2);

  input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }
`;

export function ToggleSwitch({ checked, onChange, disabled, rotulo }) {
  return (
    <ToggleWrap $disabled={disabled}>
      <input
        type="checkbox"
        checked={Boolean(checked)}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
      />
      <Track $on={Boolean(checked)} aria-hidden="true" />
      {rotulo}
    </ToggleWrap>
  );
}

/** Linha de editor estruturado (intervalos/feriados/priors) — nunca JSON cru. */
export const LinhaEditor = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  .rotulo {
    min-width: 150px;
    font-size: 12.5px;
    color: var(--ink-2);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const ListaEditor = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const BotaoRemoverLinha = styled.button`
  border: none;
  background: none;
  color: var(--ink-3);
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  display: inline-flex;

  &:hover {
    color: var(--danger-text);
    background: var(--danger-bg);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ---------------------------------------------------------------------------
// Savebar sticky por seção (§1.8) — aparece só com mudança não-salva
// ---------------------------------------------------------------------------

export const SaveBar = styled.div`
  position: sticky;
  bottom: 12px;
  z-index: 4;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  background: var(--surface);
  border: 1px solid var(--border-strong);
  border-radius: 12px;
  box-shadow: var(--shadow-2);
  padding: 10px 14px;
  font-size: 13px;
  color: var(--ink-2);

  .contagem {
    font-weight: 600;
    color: var(--ink);
  }
  .espaco {
    flex: 1 1 auto;
  }
`;

// ---------------------------------------------------------------------------
// Preview vivo (§1.9) — barra-amostra tricolor dos limiares de buffer
// ---------------------------------------------------------------------------

export const BarraAmostraFever = styled.div`
  display: flex;
  height: 14px;
  border-radius: 999px;
  overflow: hidden;
  max-width: 420px;

  span {
    height: 100%;
  }
  .verde {
    background: var(--success-solid);
    width: ${({ $amarelo }) => Math.max(Math.min($amarelo, 100), 0)}%;
  }
  .amarelo {
    background: var(--warning-solid);
    width: ${({ $amarelo, $vermelho }) =>
      Math.max(Math.min($vermelho, 100) - Math.min($amarelo, 100), 0)}%;
  }
  .vermelho {
    background: var(--danger-solid);
    width: ${({ $vermelho }) => Math.max(100 - Math.min($vermelho, 100), 0)}%;
  }
`;

/** Card honesto de item ROADMAP (§1.6 — não esconder, não prometer). */
export const CardRoadmap = styled.div`
  border: 1px dashed var(--border-strong);
  border-radius: 14px;
  background: var(--surface);
  padding: 20px;
  color: var(--ink-2);
  font-size: 13.5px;
  line-height: 1.5;

  h3 {
    margin: 0 0 8px;
    font-size: 15px;
    color: var(--ink);
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;
